import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { X } from "lucide-react";
import {
  EVENTO_APRI_PREFERENZE,
  EVENTO_CONSENSO,
  caricaAnalytics,
  leggiConsenso,
  salvaConsenso,
  type Scelte,
} from "@/lib/consent";

// Banner dei cookie: al primo accesso «Accetta» e «Rifiuta» con la stessa evidenza, X = rifiuto,
// nessuna casella già spuntata, scelte per categoria in «Personalizza». Si riapre da
// «Preferenze cookie» nel piè di pagina. Logica del consenso in src/lib/consent.ts.
// Il banner arriva già nell'HTML del server: uno script nell'head (__root.tsx) mette
// data-cookie="no" su <html> se manca una scelta valida e il CSS (.cookie-banner in styles.css)
// lo mostra subito, senza aspettare React (niente ritardo sul primo rendering).
type Stato = "iniziale" | "aperto" | "chiuso";

export function CookieBanner() {
  const [stato, setStato] = useState<Stato>("iniziale");
  const [dettagli, setDettagli] = useState(false);
  const [scelte, setScelte] = useState<Scelte>({ statistiche: false, mappe: false });
  const primoPulsante = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const salvato = leggiConsenso();
    if (salvato) {
      if (salvato.statistiche) caricaAnalytics();
      setStato("chiuso");
    } else {
      setStato("aperto");
    }
    const apri = () => {
      setScelte(leggiConsenso() ?? { statistiche: false, mappe: false });
      setDettagli(true);
      setStato("aperto");
      requestAnimationFrame(() => primoPulsante.current?.focus());
    };
    // La mappa attivata dal suo pulsante salva già una scelta: il banner non serve più.
    const chiudi = () => setStato("chiuso");
    window.addEventListener(EVENTO_APRI_PREFERENZE, apri);
    window.addEventListener(EVENTO_CONSENSO, chiudi);
    return () => {
      window.removeEventListener(EVENTO_APRI_PREFERENZE, apri);
      window.removeEventListener(EVENTO_CONSENSO, chiudi);
    };
  }, []);

  if (stato === "chiuso") return null;

  const decidi = (s: Scelte) => {
    salvaConsenso(s);
    setStato("chiuso");
    setDettagli(false);
  };

  const pulsante =
    "flex-1 inline-flex items-center justify-center rounded-md px-4 py-3 text-xs uppercase tracking-[0.16em] text-white transition-colors";

  return (
    <section
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-titolo"
      className={`cookie-banner${stato === "aperto" ? " cookie-banner--aperto" : ""} fixed z-[70] bottom-4 left-4 right-4 sm:right-auto sm:max-w-[440px] bg-white text-brand-charcoal rounded-md border border-brand-gold/30 shadow-[0_20px_50px_-15px_rgba(42,37,32,0.35)] p-5`}
    >
      <button
        type="button"
        onClick={() => decidi({ statistiche: false, mappe: false })}
        aria-label="Chiudi: continua senza accettare"
        className="absolute top-2.5 right-2.5 w-9 h-9 rounded-full flex items-center justify-center text-brand-warm-gray hover:bg-brand-cream hover:text-brand-charcoal transition-colors"
      >
        <X size={18} />
      </button>
      <h2 id="cookie-titolo" className="font-display text-xl pr-10">Cookie</h2>
      <p className="mt-2 text-sm text-brand-warm-gray leading-relaxed">
        Usiamo cookie tecnici, necessari al sito. Solo se accetti useremo anche Google Analytics
        per le statistiche delle visite e mostreremo la mappa interattiva di Google Maps.{" "}
        <Link to="/cookie" className="text-brand-red underline underline-offset-2">
          Cookie policy
        </Link>
      </p>

      {dettagli && (
        <fieldset className="mt-4 space-y-3 text-sm">
          <legend className="sr-only">Scegli quali cookie accettare</legend>
          <div className="flex items-start gap-3">
            <input type="checkbox" checked disabled className="mt-1 accent-brand-red" id="ck-tecnici" />
            <label htmlFor="ck-tecnici">
              <span className="font-medium">Tecnici</span>
              <span className="block text-brand-warm-gray text-xs">Sempre attivi: ricordano la tua scelta e le richieste in corso.</span>
            </label>
          </div>
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="ck-statistiche"
              className="mt-1 accent-brand-red"
              checked={scelte.statistiche}
              onChange={(e) => setScelte((s) => ({ ...s, statistiche: e.target.checked }))}
            />
            <label htmlFor="ck-statistiche">
              <span className="font-medium">Statistiche</span>
              <span className="block text-brand-warm-gray text-xs">Google Analytics: quante persone visitano il sito e quali pagine.</span>
            </label>
          </div>
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="ck-mappe"
              className="mt-1 accent-brand-red"
              checked={scelte.mappe}
              onChange={(e) => setScelte((s) => ({ ...s, mappe: e.target.checked }))}
            />
            <label htmlFor="ck-mappe">
              <span className="font-medium">Mappa</span>
              <span className="block text-brand-warm-gray text-xs">Google Maps interattiva nella pagina Contatti.</span>
            </label>
          </div>
        </fieldset>
      )}

      <div className="mt-4 flex gap-3">
        <button
          ref={primoPulsante}
          type="button"
          onClick={() => decidi({ statistiche: false, mappe: false })}
          className={`${pulsante} bg-brand-charcoal hover:bg-black`}
        >
          Rifiuta
        </button>
        <button
          type="button"
          onClick={() => decidi({ statistiche: true, mappe: true })}
          className={`${pulsante} bg-brand-red hover:bg-brand-red-hover`}
        >
          Accetta
        </button>
      </div>
      <div className="mt-3 text-center">
        {dettagli ? (
          <button
            type="button"
            onClick={() => decidi(scelte)}
            className="text-xs uppercase tracking-[0.16em] text-brand-charcoal underline underline-offset-4 hover:text-brand-red"
          >
            Salva le mie scelte
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setDettagli(true)}
            className="text-xs uppercase tracking-[0.16em] text-brand-charcoal underline underline-offset-4 hover:text-brand-red"
          >
            Personalizza
          </button>
        )}
      </div>
    </section>
  );
}
